import React from 'react'
// Components
import {
  IndexLayoutOldTitle,
  GenericErrorView,
  RouteParams,
  routes,
  useQueryParams,
  IndexLayoutExtraProps,
  IndexLayoutContent,
  removeActiveAuthorization,
  useUserProfile,
} from '@sketch/modules-common'
import {
  PersonAvatar,
  WorkspaceLogo,
  useModalContext,
  LoadingState,
} from '@sketch/components'
import { SsoTag } from 'modules/workspace/containers/WorkspacesDropdown'
import {
  goToLinkAccountUrl,
  useLinkAccountConfirmation,
} from 'modules/sso/operations'
import {
  Wrapper,
  StyledPanel,
  LinkedWorkspacesAvatarsContainer,
  StyledWorkspaceLogo,
  WorkspaceName,
  Description,
  StyledPanelFooter,
  StyledButton,
  Container,
  Line,
  LineTitle,
  LineContent,
  Name,
  StyledPanelBody,
  WorkspaceLineWrapper,
  StyledLink,
  StyledFlatButton,
} from './LinkAccountConfirmationPage.styles'
import { useHistory, useParams } from 'react-router-dom'
import { useToast } from '@sketch/toasts'
import { WorkspaceMinimalIdentityFragment } from '@sketch/gql-types'
import LinkedWorkspacesModal from 'modules/sso/modals/LinkedWorkspacesModal'

const WorkspaceLines = ({
  workspaces,
  userEmail,
}: {
  workspaces: WorkspaceMinimalIdentityFragment[]
  userEmail: string
}) => {
  const { showModal } = useModalContext()

  const maxWorkspaceAvatars = workspaces.length > 3 ? 3 : workspaces.length

  const linkedWorkspaces = workspaces
    .slice(0, maxWorkspaceAvatars)
    .map(workspace => (
      <WorkspaceLineWrapper key={workspace.identifier}>
        <StyledWorkspaceLogo
          key={workspace.identifier}
          size="24px"
          workspaceName={workspace.name}
          src={workspace.avatar?.small}
        />
        <WorkspaceName>{workspace.name}</WorkspaceName>
      </WorkspaceLineWrapper>
    ))

  const numWorkspacesCropped = workspaces.length - 3

  return (
    <Wrapper data-testid="linked-workspaces">
      {linkedWorkspaces}
      {workspaces.length > 3 && (
        <StyledLink
          variant="secondary"
          onClick={() =>
            showModal(LinkedWorkspacesModal, {
              userEmail,
              workspaces,
            })
          }
          isUnderlined
          external
        >
          {numWorkspacesCropped} More…
        </StyledLink>
      )}
    </Wrapper>
  )
}

const LinkedWorkspaces = ({
  workspaces,
  userEmail,
}: {
  workspaces: WorkspaceMinimalIdentityFragment[]
  userEmail: string
}) => {
  if (workspaces.length === 0) return null

  return (
    <LinkedWorkspacesAvatarsContainer>
      <WorkspaceLines workspaces={workspaces} userEmail={userEmail} />
    </LinkedWorkspacesAvatarsContainer>
  )
}

export const LinkAccountConfirmationPage = (props: IndexLayoutExtraProps) => {
  const { useOverrideLayoutProps } = props
  const { showToast } = useToast()
  const { workspaceId } = useParams<RouteParams<'SSO_LINK_ACCOUNT'>>()
  const queryParams = useQueryParams<'SSO_LINK_ACCOUNT'>()
  const { data: userProfile, loading, client } = useUserProfile()
  const result = useLinkAccountConfirmation(workspaceId)
  const history = useHistory()

  useOverrideLayoutProps({ variant: 'orange' })

  if (result.type === 'loading' || loading) {
    return <LoadingState />
  }

  if (result.type === 'error') {
    if (result.message) showToast(result.message)

    return <GenericErrorView isInLayout />
  }

  const user = userProfile?.me

  return (
    <IndexLayoutContent center="horizontal" marginTop paddingHorizontal>
      <StyledPanel>
        <IndexLayoutOldTitle>Link Your Accounts</IndexLayoutOldTitle>
        <StyledPanelBody>
          <Description>
            Link your accounts to quickly switch between all the Workspaces
            you’ve joined.
          </Description>
          <br />
          <Description>Accounts cannot be linked later.</Description>
          <Container data-testid="signin-workspace">
            <Line>
              <LineTitle>Link</LineTitle>
              <LineContent>
                <WorkspaceLogo
                  src={result.signInWorkspace.avatar?.small}
                  workspaceName={result.signInWorkspace.name}
                  size="24px"
                  UNSAFE_ADD_MARGIN
                />
                <span>{result.signInWorkspace.name}</span>
                <SsoTag />
              </LineContent>
            </Line>
            <Line>
              <LineTitle>to Sketch Account</LineTitle>
              <LineContent>
                {user?.avatar?.small ? (
                  <PersonAvatar
                    size="24px"
                    flavour="image"
                    src={user.avatar.small}
                    name={user?.name ?? ''}
                  />
                ) : (
                  <PersonAvatar
                    size="24px"
                    flavour="color"
                    name={user?.name ?? ''}
                  />
                )}
                <Name>{user?.email ?? ''}</Name>
              </LineContent>
            </Line>
            <Line>
              <LineTitle>which has access to</LineTitle>
              <LineContent>
                <LinkedWorkspaces
                  workspaces={result.linkedWorkspaces}
                  userEmail={user?.email ?? ''}
                />
              </LineContent>
            </Line>
          </Container>
        </StyledPanelBody>
        <StyledPanelFooter>
          <StyledButton
            size="40"
            variant="primary"
            onClick={() =>
              goToLinkAccountUrl({
                url: result.signInWorkspace.ssoStartUrl,
                linkedAccountToken: result.ssoLinkToken,
                ...queryParams,
              })
            }
          >
            Link Account
          </StyledButton>
          <StyledFlatButton
            variant="primary"
            onClick={() => {
              /**
               * Move to the TOS check
               */
              history.push(
                routes.SSO_TOS.create({
                  workspaceId,
                  query: queryParams,
                })
              )

              /**
               * We need to remove the active authorization (personal)
               * if any because on this view the SSO account wasn't linked
               * with the personal one and if any traces of the personal token
               * are send as secondary authorization when fetching
               * BE will return an error
               */
              removeActiveAuthorization(client)
            }}
          >
            Don&apos;t link accounts
          </StyledFlatButton>
        </StyledPanelFooter>
      </StyledPanel>
    </IndexLayoutContent>
  )
}
